/**
 * KiBeobachtungen — the KI-Beobachtungen card (Story UI-U7, AC 6, conditional).
 *
 * Rendered ONLY when the optional `ki_beobachtungen` wire field is present (ABSENT
 * today — the future-insights seam; the screen feature-detects and does not mount this
 * at all when the field is undefined). InsightCard shape: title + soft category badge
 * (Chance/Risiko/Hinweis) + text + Konfidenz. tone map: chance→success, risiko→danger,
 * hinweis→info — a SOFT badge, never a filled status pill (design.md §1).
 *
 * [Source: brokly-design-reference/components/brokly/insight-card.tsx]
 * [Source: docs/stories/ui-u7-portfolio.md AC 6]
 */

import { Sparkles } from 'lucide-react';
import type { PortfolioStatisticsDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge';

type KiBeobachtung = NonNullable<PortfolioStatisticsDto['ki_beobachtungen']>[number];

const TONE_META: Record<KiBeobachtung['tone'], { variant: BadgeVariant; label: string }> = {
  chance: { variant: 'success', label: 'Chance' },
  risiko: { variant: 'danger', label: 'Risiko' },
  hinweis: { variant: 'info', label: 'Hinweis' },
};

export function KiBeobachtungen({ items }: { items: KiBeobachtung[] }) {
  return (
    <Card className="rounded-xl p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Sparkles className="size-4 text-primary" aria-hidden /> KI-Beobachtungen
      </div>
      <ul className="space-y-3">
        {items.map((b) => {
          const meta = TONE_META[b.tone];
          return (
            <li key={b.id} className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                {b.titel}
                <Badge variant={meta.variant}>{meta.label}</Badge>
                {b.konfidenz !== null && (
                  <Badge variant="neutral" className="ml-auto text-[10px]">
                    Konfidenz {b.konfidenz}%
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-[13px] leading-relaxed text-foreground/80">{b.text}</p>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
